import { useMemo, useState } from 'react';

import path from 'path';

import { Stack } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, useColDefs } from '~components';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType, StatusVariant } from '~enums';
import { OffsetPagedResults } from '~interfaces';
import OfflineKey from '~interfaces/offlineKey';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import OfflineAccessService from './services/offlineAccessService';

const offlineAccessService = getService(OfflineAccessService);

interface Props {
	showAddOfflineKeyButton?: boolean;
	onAddOfflineKeyButtonClicked?: () => void;
	source: 'device' | 'key';
	deviceId?: string;
	keyId?: string;
	overwrittenClientId?: string;
	refreshTrigger?: number;
	enableRemoveOfflineKey?: boolean;
	onRemoveOfflineKey?: (id: string) => void;
}

const OfflineAccessDataGrid = ({
	source,
	deviceId,
	keyId,
	overwrittenClientId,
	refreshTrigger = 0,
	showAddOfflineKeyButton = false,
	onAddOfflineKeyButtonClicked = () => {},
	enableRemoveOfflineKey = false,
	onRemoveOfflineKey = () => {},
}: Props) => {
	const { t } = useTranslation();
	const persistenceKey = `offlineAccess-${source}-${deviceId}-${keyId}-${overwrittenClientId}`;
	const isDeviceSource = source === 'device';

	const apiRef = useGridApiRef();
	const [searchParams] = useSearchParams();
	const [totalCount, setTotalCount] = useState<number>(0);
	const cacheKey = offlineAccessService.getOfflineKeysCacheKey(
		searchParams,
		source,
		refreshTrigger,
		deviceId,
		keyId,
		overwrittenClientId
	);
	const { data, error, isLoading, isValidating } = useSWR(
		cacheKey,
		(
			_args: (string | number | URLSearchParams | undefined)[],
			options: { signal?: AbortSignal }
		) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;

			if (isDeviceSource && deviceId == null) {
				throw new Error('Device id is required to load offline keys for device source.');
			}
			if (!isDeviceSource && keyId == null) {
				throw new Error('Key id is required to load offline keys for device source.');
			}

			return offlineAccessService.getOfflineKeys(
				{
					page: page,
					pageSize: pageSize,
					searchQuery: searchQuery,
					organisationId: overwrittenClientId,
					resourceGroupId: undefined,
					deviceIds: deviceId ? [deviceId] : undefined,
					keyIds: keyId ? [keyId] : undefined,
				},
				options.signal
			);
		},
		{
			onSuccess: (response: OffsetPagedResults<OfflineKey>) => {
				setTotalCount(response?.total ?? 0);
			},
			keepPreviousData: false,
			use: [cancellationMiddleware],
		}
	);

	const queryParamsGetter = () =>
		overwrittenClientId ?
			([[queryParameters.OverwrittenClientId, overwrittenClientId]] as ParamKeyValuePair[])
		:	([] as ParamKeyValuePair[]);

	const colDefs = useColDefs<OfflineKey>();
	const columns = useMemo((): GridColDef<OfflineKey>[] => {
		return [
			...(!isDeviceSource ?
				[
					colDefs.hardwareId(
						queryParamsGetter,
						true,
						(value: OfflineKey) => value.device.id,
						(value: OfflineKey) => value.device.hardwareId
					),
				]
			:	[]),
			colDefs.name(
				true,
				path.join(
					!isDeviceSource ? pagePathSegments.Devices : pagePathSegments.Keys,
					':id',
					pagePathSegments.General
				),
				queryParamsGetter,
				(value: OfflineKey) => (!isDeviceSource ? value.device.id : value.key.id),
				undefined,
				(value: OfflineKey) => (!isDeviceSource ? value.device.name : value.key.name)
			),
			...(isDeviceSource ?
				[
					colDefs.serialNumber(
						(value: OfflineKey) => value.key.serialNumber ?? '',
						(value: OfflineKey) => value.key.id,
						queryParamsGetter,
						true
					),
				]
			:	[]),
			...(enableRemoveOfflineKey ?
				[
					colDefs.rowActions((row) => {
						return [
							{
								key: 'remove-offline-key',
								variant: StatusVariant.Destructive,
								icon: <VectorImage name='icon/remove' />,
								label: t('remove', { ns: 'actions' }),
								onClick: () => onRemoveOfflineKey(row.id.toString()),
							},
						];
					}),
				]
			:	[]),
		];
	}, [colDefs, isDeviceSource, t, overwrittenClientId]);

	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
	];

	const actionBarActions = [
		...(showAddOfflineKeyButton ?
			[
				{
					id: 'addOfflineKey',
					label:
						t('add', { ns: 'actions' }) +
						' ' +
						t('key.labels.single', { ns: 'entities' }).toLowerCase(),
					variant: StatusVariant.Neutral,
					onClick: onAddOfflineKeyButtonClicked,
					icon: <VectorImage name='icon/plus' />,
				},
			]
		:	[]),
	];

	return (
		<Stack direction='column' sx={{ width: 1 }} gap={Dimens.spacingMd}>
			<DataGridToolbar filters={actionBarFilters} actions={actionBarActions} />
			<DataGrid
				apiRef={apiRef}
				persistenceKey={persistenceKey}
				autoHeight
				disableRowSelectionOnClick
				error={error != null}
				loading={isLoading || isValidating}
				columns={columns}
				rows={data?.results ?? []}
				rowCount={totalCount}
				noDataTitle={t('noDataTitle', { ns: 'table' })}
				noDataDescription={t('noDataDescription', { ns: 'table' })}
				paginationModel={{
					page: Number(searchParams.get(queryParameters.Page) ?? '1'),
					pageSize: Number(searchParams.get(queryParameters.PageSize) ?? '10'),
				}}
			/>
		</Stack>
	);
};

export default OfflineAccessDataGrid;
